// 頁型模板⑥：redirect（1 筆 route）。
//
// NEW-UI-IMPLEMENTATION-CHECKLIST §5.2 明訂：redirect 頁型的狀態集只有 `redirect-behavior`，
// 且**不建 React 頁**。所以這個模板刻意不重建任何原系統畫面 —— 它只是路由層需要有東西可以
// render 時的一塊說明，說清楚「這個 pageId 在原系統只做轉址」以及轉去哪裡。
//
// 真的要不要在 React 這一側執行轉址，是路由層的決定（可能是 <Navigate>，也可能根本不註冊
// 這條 route）。模板不自己呼叫 navigate()：那會讓這一頁在 Storybook／截圖驗證時無法停留。
//
// 【證據等級】§7 第 3 級（原始碼／盤點結果）。

import type { ReactElement } from 'react';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { ContentPanel } from '@/components/layouts';
import { t } from '@/messages/zh-TW';
import type { PageTemplateProps } from './types';

export function RedirectPage({ route }: PageTemplateProps): ReactElement {
  const pageId = route.pageId;
  return (
    // 模板只負責內容區：全站外框（ApplicationShell／Navbar／SidebarMenu／Breadcrumb）
    // 由路由層的 AppLayout 提供，模板再包一層會變成殼中殼。
    // 這個 <div> 是整頁的根節點，data-ui-id 就是 §10.1 說的 pageId 本身。
    <div data-ui-id={pageId}>
      <ContentPanel
        uiId={`${pageId}-panel`}
        title={route.title}
        headingLevel={panelHeadingLevelFor(pageId)}
      >
        <p data-ui-id={`${pageId}-redirect-notice`} className="text-sm">
          {t('redirect.notice')}
        </p>
        <dl data-ui-id={`${pageId}-redirect-detail`} className="mt-3 flex flex-col gap-1 text-sm">
          <dt data-ui-id={`${pageId}-redirect-target-label`} className="text-ink-muted">
            {t('redirect.target')}
          </dt>
          <dd data-ui-id={`${pageId}-redirect-target`}>
            {route.redirectTarget ?? t('unresolved.none')}
          </dd>
          <dt data-ui-id={`${pageId}-redirect-legacy-url-label`} className="text-ink-muted">
            {t('unresolved.legacyUrl')}
          </dt>
          <dd data-ui-id={`${pageId}-redirect-legacy-url`}>
            {route.legacyUrl ?? t('unresolved.none')}
          </dd>
        </dl>
      </ContentPanel>
    </div>
  );
}
